/**
=========================================================
* ooGoal - v5.0.0
=========================================================

* Product Page: https://oogoal.com
* Copyright 2022 Onza Technologies

=========================================================

* The above copyright must be included
*/

import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import OGBox from "assets/theme/components/OGBox";
import OGTypography from "assets/theme/components/OGTypography";
import OGButton from "assets/theme/components/OGButton";
import SidenavCollapse from "assets/theme/components/OGSidenav/SidenavCollapse";
import SidenavRoot from "assets/theme/components/OGSidenav/SidenavRoot";
import sidenavLogoLabel from "assets/theme/components/OGSidenav/styles/sidenav";
import { useThemeUIController, setCollapseSidenav } from "context";

function Sidenav({ theme, color, brand, brandName, routes, ...rest }) {
  const { palette } = theme;
  const { green, red } = palette;
  const [controller, dispatch] = useThemeUIController();
  const { collapseSidenav, darkMode } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  const closeSidenav = () => setCollapseSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the collapse state of the sidenav.
    function handleCollapseSidenav() {
      setCollapseSidenav(dispatch, window.innerWidth < 1200);
    }
    /** 
     The event listener that's calling the handleCollapseSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleCollapseSidenav);
    // Call the handleCollapseSidenav function to set the state with the initial value.
    handleCollapseSidenav();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleCollapseSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, accessTier }) => {
      let returnValue;
      if (type === "collapse") {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <OGTypography
            key={key}
            color={darkMode ? green.main : red.main}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={0}
            mt={2}
            mb={1}
          >
            {title}
          </OGTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} light={darkMode ? "white" : "black"} />;
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ collapseSidenav, darkMode }}>
      <OGBox pt={1} pb={1} px={4} textAlign="center">
        <OGBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <OGTypography variant="h6" color={darkMode ? "white" : "black"}>
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </OGTypography>
        </OGBox>
        <OGBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <OGBox component="img" src={brand} alt="ooGoal" width="4rem" />}
          <OGBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { collapseSidenav })}
          >
            <OGTypography
              component="h4"
              variant="h4"
              fontWeight="bold"
              color={darkMode ? "white" : "black"}
            >
              {brandName}
            </OGTypography>
          </OGBox>
        </OGBox>
      </OGBox>
      <OGBox sx={{ width: "200px" }} ml={1} mr={1} mt={1}>
        <List>{renderRoutes}</List>
        <OGBox pt={4} pb={2}>
          <OGButton component="a" href="/oogoal" rel="noreferrer" variant="gradient" fullWidth>
            We will be back soon!
          </OGButton>
        </OGBox>
      </OGBox>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  brand: "ooGoal",
};

Sidenav.propTypes = {
  theme: PropTypes.object.isRequired,
  color: PropTypes.oneOf(["green", "light", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
